import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useBillingApi } from '../../../../common/hooks/use-billing-api.hook';

interface EmbeddedUpgradeModalProps {
  /** Plan id to check out (self-serve plan). Mutually exclusive with quoteId. */
  planId?: string | null;
  /** Billing cycle for a plan checkout (defaults to monthly). */
  interval?: 'month' | 'year';
  /** Accepted-quote id to check out against its custom price. */
  quoteId?: string | null;
  onClose: () => void;
  /** Fired once payment completes (redirect_on_completion is 'never'). */
  onComplete: () => void;
}

/**
 * In-app Stripe checkout: mounts Stripe's EmbeddedCheckout in a dialog so the
 * vendor pays without leaving the app. The publishable key is fetched from the
 * backend (no build-time env). The subscription is provisioned by the webhook;
 * onComplete lets the parent refetch/close.
 */
export const EmbeddedUpgradeModal: React.FC<EmbeddedUpgradeModalProps> = ({ planId, interval = 'month', quoteId, onClose, onComplete }) => {
  const billingApi = useBillingApi();

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const open = !!planId || !!quoteId;

  // Reset transient state each time the modal closes.
  useEffect(() => {
    if (!open) { setError(null); setCompleted(false); }
  }, [open]);

  // Load the publishable key (once) when the modal first opens.
  useEffect(() => {
    if (!open || stripePromise) return;
    let active = true;
    billingApi.getBillingConfig()
      .then(({ publishableKey }) => {
        if (!active) return;
        if (!publishableKey) { setError('Payments are not configured yet. Please contact support.'); return; }
        setStripePromise(loadStripe(publishableKey));
      })
      .catch(() => active && setError('Could not start checkout. Please try again.'));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchClientSecret = useCallback(async () => {
    const { clientSecret } = quoteId
      ? await billingApi.createQuoteCheckoutSession(quoteId)
      : await billingApi.createEmbeddedCheckoutSession(planId as string, interval);
    return clientSecret;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, quoteId, interval]);

  const handleComplete = useCallback(() => {
    setCompleted(true);
    onComplete();
  }, [onComplete]);

  const options = useMemo(() => ({ fetchClientSecret, onComplete: handleComplete }), [fetchClientSecret, handleComplete]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="gap-0 p-0 sm:max-w-lg">
        <DialogHeader className="px-4 py-3">
          <DialogTitle>Complete your upgrade</DialogTitle>
        </DialogHeader>

        <div className="min-h-[320px]">
          {error ? (
            <div className="p-8 text-center text-sm text-muted-foreground">{error}</div>
          ) : completed ? (
            <div className="p-10 text-center">
              <p className="mb-1 text-sm font-medium">Payment received 🎉</p>
              <p className="text-sm text-muted-foreground">Activating your subscription…</p>
            </div>
          ) : stripePromise ? (
            /* Stripe renders its own checkout inside an iframe — the container is
               ours to style, the form internals are not. */
            <EmbeddedCheckoutProvider key={`${planId ?? quoteId ?? 'none'}-${interval}`} stripe={stripePromise} options={options}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          ) : (
            <div className="flex justify-center p-12">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmbeddedUpgradeModal;
